import { useEffect, useMemo, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import ApproachDistancesParser from "@/lib/approachDistancesParser"
import { ApproachOrigin } from "./approachOrigins"
import { ProximityPoint } from "./vehicleSuggestions"
import { RoadDistance } from "./plannerPointRanking"

const DEBOUNCE_MS = 400

// Flota jedzie, więc policzony dojazd się starzeje — a klucz żądania celowo nie
// zawiera już współrzędnych (patrz requestKey), więc odświeżenie musi przyjść z
// zegara. Wymiana jest jawna: zafałszowanie kilometrów ograniczone do jednego
// interwału (autostradą ~2 km) w zamian za STAŁY koszt — 30 macierzy na godzinę
// na zakładkę z ustawionym punktem, niezależnie od tego, ile pojazdów się
// rusza. Limiter HERE po stronie gps (10 000 zapytań / 4 h) jest wspólny z
// liczeniem tras kierowców, więc to on jest tu budżetem, nie planer.
const REFRESH_INTERVAL_MS = 120_000

// Wspólna pusta mapa: zwracanie new Map() przy każdym renderze niepotrzebnie
// zmieniałoby tożsamość wyniku.
const NO_DISTANCES: Map<string, RoadDistance> = new Map()

// Dojazd drogowy dla czoła rankingu. Świadomie NIE zgłasza błędów: gdy gps nie
// odpowie (niewdrożona komenda, timeout, odmowa HERE), planer zostaje na
// odległości w linii prostej i dyspozytor nic nie traci.
export const useApproachDistances = (
  point: ProximityPoint | null,
  day: Date | null,
  origins: ApproachOrigin[],
): Map<string, RoadDistance> => {
  // Do CZEGO te kilometry są zmierzone: punkt plus dzień. Dzień jest częścią
  // tożsamości pomiaru, bo to on wybiera pozycje odniesienia — po zmianie daty
  // etykieta natychmiast pokazuje nową miejscowość, więc kilometry z poprzedniej
  // daty nie są nieświeże, są po prostu złe.
  const measurementKey = useMemo(
    () =>
      point
        ? `${point.lat.toString()},${point.lon.toString()}@${(day?.getTime() ?? 0).toString()}`
        : "",
    [point, day],
  )

  // Wynik trzymany RAZEM z kluczem pomiaru. Samo anulowanie odpowiedzi nie
  // wystarczało: stare kilometry zostawały w stanie i renderowały się pod
  // nowym punktem przez cały debounce plus czas odpowiedzi.
  const [measured, setMeasured] = useState<{
    measurementKey: string
    distances: Map<string, RoadDistance>
  }>({ measurementKey: "", distances: NO_DISTANCES })

  const [refreshTick, setRefreshTick] = useState(0)
  useEffect(() => {
    if (!measurementKey) return
    const interval = setInterval(
      () => setRefreshTick((tick) => tick + 1),
      REFRESH_INTERVAL_MS,
    )
    return () => clearInterval(interval)
    // Reset zegara przy zmianie punktu jest zamierzony: świeżo policzonego
    // dojazdu nie ma po co odświeżać sekundę po ustawieniu punktu.
  }, [measurementKey])

  // Klucz żądania: punkt (z dniem) plus ZBIÓR jidów topki. Współrzędnych
  // origins tu NIE ma, bo wiązały koszt z ruchem floty — auto na autostradzie
  // przekraczało kubełek co ~50 s, więc dziesięć origins dawało ~12 żądań na
  // minutę z jednej zakładki. Jidy są sortowane, żeby zamiana miejsc dwóch
  // prawie równo odległych pojazdów sama z siebie nie wywoływała żądania.
  const requestKey = useMemo(
    () =>
      measurementKey
        ? JSON.stringify([
            measurementKey,
            origins.map((origin) => origin.chatJid).sort(),
          ])
        : "",
    [measurementKey, origins],
  )

  useEffect(() => {
    const client = window.XMPP
    if (!point || origins.length === 0 || !client) {
      // Bez zmiany stanu, gdy i tak nic nie mamy — inaczej samo wejście w planer
      // bez punktu kosztuje dodatkowy render całego gridu.
      setMeasured((previous) =>
        previous.measurementKey === measurementKey &&
        previous.distances === NO_DISTANCES
          ? previous
          : { measurementKey, distances: NO_DISTANCES },
      )
      return
    }

    let isCancelled = false
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const stanza = await AdHocCommands.getVehicleApproachDistances(
            client,
            { lat: point.lat, lon: point.lon },
            origins,
          )
          if (isCancelled) return
          setMeasured({
            measurementKey,
            distances: ApproachDistancesParser.parse(stanza),
          })
        } catch (error) {
          console.warn("Failed to load approach distances", error)
          if (isCancelled) return
          setMeasured({ measurementKey, distances: NO_DISTANCES })
        }
      })()
    }, DEBOUNCE_MS)

    return () => {
      isCancelled = true
      clearTimeout(timer)
    }
    // requestKey zamyka w sobie punkt, dzień i zbiór jidów topki; refreshTick
    // dokłada wymuszone odświeżenie co REFRESH_INTERVAL_MS. Aktualne
    // współrzędne origins wchodzą do żądania (efekt czyta je z bieżącego
    // renderu), ale NIE wywołują go — tego pilnuje zegar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, refreshTick])

  // Porównujemy tylko klucz pomiaru, nie zbiór jidów: odświeżenie topki po
  // zmianie składu floty nie ma migać wszystkimi etykietami z powrotem na
  // linię prostą.
  return measured.measurementKey === measurementKey
    ? measured.distances
    : NO_DISTANCES
}
